import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Smartphone, Sparkles, Rocket, MousePointerClick, Check, Star } from "lucide-react";
import { MarketingLayout } from "@/components/MarketingLayout";
import { PhoneMockup } from "@/components/PhoneMockup";
import client from "@/lib/api";
import { toast } from "sonner";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5, ease: [0.16, 1, 0.3, 1] } }),
};

export default function Home() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const joinWaitlist = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await client.post("/waitlist", { email, source: "home_hero" });
      toast.success("You're on the list! Check your inbox soon.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <MarketingLayout>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-6xl px-6 pt-12 pb-8 md:pt-20">
          <div className="grid items-center gap-12 md:grid-cols-2">
            <div>
              <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0}
                className="inline-flex items-center gap-2 rounded-full border border-black/5 bg-white px-3 py-1.5 text-xs font-semibold text-ho-ink shadow-sm">
                <Smartphone className="w-3.5 h-3.5 text-ho-primary" /> Built to be used on your phone
              </motion.div>

              <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1}
                className="font-display mt-5 text-5xl font-black leading-[0.95] tracking-tighter md:text-6xl">
                Build a real website
                <br />
                <span className="text-ho-primary">from your phone.</span>
              </motion.h1>

              <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2}
                className="mt-5 max-w-md text-lg leading-relaxed text-ho-muted">
                Describe what you want in plain language. Watch it appear in a live mobile preview. Deploy to a real URL with one tap. No laptop, ever.
              </motion.p>

              <motion.form variants={fadeUp} initial="hidden" animate="show" custom={3}
                onSubmit={joinWaitlist} className="mt-7 flex flex-col gap-3 sm:flex-row">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  data-testid="hero-email-input"
                  className="h-13 flex-1 rounded-full border border-black/10 bg-white px-5 py-3.5 text-[15px] outline-none focus:border-ho-primary"
                />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="hero-waitlist-submit"
                  className="tap-scale inline-flex h-[52px] items-center justify-center gap-2 rounded-full bg-ho-primary px-6 text-[15px] font-semibold text-white disabled:opacity-60"
                >
                  {busy ? "Joining…" : "Get early access"}
                </button>
              </motion.form>

              <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4}
                className="mt-5 flex items-center gap-4">
                <button
                  onClick={() => navigate("/login")}
                  data-testid="hero-start-building"
                  className="tap-scale inline-flex items-center gap-1.5 text-sm font-semibold text-ho-ink hover:text-ho-primary"
                >
                  Start building free <ArrowRight className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-1 text-sm text-ho-muted">
                  <div className="flex">
                    {[0, 1, 2, 3, 4].map((s) => (
                      <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <span className="ml-1">Loved by 1,000+ builders</span>
                </div>
              </motion.div>
            </div>

            <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} className="relative">
              <div className="absolute -inset-6 -z-10 rounded-[48px] bg-ho-primary/5 blur-2xl" />
              <PhoneMockup />
            </motion.div>
          </div>
        </div>

        {/* trust strip */}
        <div className="mx-auto max-w-6xl px-6 pb-6">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 rounded-3xl border border-black/5 bg-white/60 px-6 py-5 text-center backdrop-blur">
            <Stat value="250K+" label="creators" />
            <Divider />
            <Stat value="1M+" label="sites shipped" />
            <Divider />
            <Stat value="0" label="laptops needed" />
            <Divider />
            <Stat value="<10s" label="to first draft" />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-6xl px-6 py-20">
        <SectionHeader
          overline="The flow"
          title="Three taps to live"
          subtitle="No dev environment, no code, no zooming. Just your thumb and an idea."
        />
        <div className="mt-12 grid gap-4 md:grid-cols-3">
          {[
            { icon: Sparkles, title: "Describe it", body: "Type what you want in plain words. The AI builds a complete, styled site in seconds." },
            { icon: MousePointerClick, title: "Tap to refine", body: "Tap any part of the live preview and describe a change. The AI edits just that piece." },
            { icon: Rocket, title: "Deploy in one tap", body: "Publish to a real live URL instantly. Share it before your bus reaches the next stop." },
          ].map((s, i) => (
            <motion.div key={s.title} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className="relative rounded-3xl border border-black/5 bg-white p-6">
              <div className="grid place-items-center w-12 h-12 rounded-2xl bg-ho-primary/10">
                <s.icon className="w-6 h-6 text-ho-primary" />
              </div>
              <div className="mt-4 font-display text-xl font-bold">{s.title}</div>
              <p className="mt-2 text-[15px] leading-relaxed text-ho-muted">{s.body}</p>
              <span className="absolute right-6 top-6 font-mono text-sm text-black/15">0{i + 1}</span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURE BENTO */}
      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <SectionHeader overline="Why HandsOn" title="A builder that respects your thumb" subtitle="Every interaction was designed for a phone first — not squeezed down from a desktop tool." />
          <div className="mt-12 grid gap-4 md:grid-cols-3 md:auto-rows-[200px]">
            <BentoCard className="md:col-span-2 md:row-span-2 bg-ho-ink text-white" data-testid="bento-preview">
              <div className="flex h-full flex-col justify-between">
                <div>
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">Live mobile preview</span>
                  <h3 className="font-display mt-3 text-3xl font-bold tracking-tight">See exactly what you ship</h3>
                  <p className="mt-3 max-w-md text-white/70">No pinching, no zooming. The preview fills your screen and you interact with it like a real app.</p>
                </div>
                <div className="mt-6 flex gap-2">
                  {["Portfolio", "Storefront", "Booking", "Waitlist"].map((t) => (
                    <span key={t} className="rounded-full bg-white/10 px-3 py-1.5 text-xs font-medium">{t}</span>
                  ))}
                </div>
              </div>
            </BentoCard>
            <BentoCard><MiniFeature icon={MousePointerClick} title="Tap-to-edit" body="Point at anything. Describe the change. Done." /></BentoCard>
            <BentoCard><MiniFeature icon={Rocket} title="One-tap deploy" body="Real live URLs via Netlify, instantly." /></BentoCard>
            <BentoCard className="md:col-span-3 bg-ho-primary text-white">
              <div className="flex h-full flex-col items-start justify-center gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="font-display text-2xl font-bold">Ready to build something?</h3>
                  <p className="text-white/80">Your first site is free. No card required.</p>
                </div>
                <button onClick={() => navigate("/login")} data-testid="bento-cta"
                  className="tap-scale rounded-full bg-white px-6 py-3 text-sm font-semibold text-ho-primary">
                  Start building
                </button>
              </div>
            </BentoCard>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="mx-auto max-w-6xl px-6 py-20">
        <SectionHeader overline="Word of mouth" title="Freelancers & agencies love it" subtitle="Our #1 growth channel is people telling their friends." />
        <div className="mt-12 grid gap-4 md:grid-cols-3">
          {[
            { q: "Built my client's booking page on the train. Sent the link before I got off. Wild.", n: "Priya S.", r: "Freelance designer" },
            { q: "I don't own a laptop anymore. HandsOn is genuinely my whole studio now.", n: "Marcus T.", r: "Photographer" },
            { q: "We spin up landing pages for agency clients in minutes, all from mobile.", n: "Lena K.", r: "Agency owner" },
          ].map((t, i) => (
            <motion.div key={t.n} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className="rounded-3xl border border-black/5 bg-white p-6">
              <div className="flex">{[0, 1, 2, 3, 4].map((s) => <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" />)}</div>
              <p className="mt-4 text-[15px] leading-relaxed text-ho-ink">"{t.q}"</p>
              <div className="mt-5 flex items-center gap-3">
                <div className="grid place-items-center w-9 h-9 rounded-full bg-ho-primary/10 font-display text-sm font-bold text-ho-primary">
                  {t.n[0]}
                </div>
                <div>
                  <div className="text-sm font-semibold">{t.n}</div>
                  <div className="text-xs text-ho-muted">{t.r}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="mx-auto max-w-6xl px-6 pb-8">
        <div className="relative overflow-hidden rounded-[36px] bg-ho-ink px-8 py-16 text-center text-white grain">
          <h2 className="font-display relative text-4xl font-black tracking-tighter md:text-5xl">
            Your idea. Your phone.<br />Live in minutes.
          </h2>
          <p className="relative mx-auto mt-4 max-w-md text-white/70">
            Join thousands building real websites without ever opening a laptop.
          </p>
          <button onClick={() => navigate("/login")} data-testid="final-cta"
            className="tap-scale relative mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 font-semibold text-ho-ink">
            Start building free <ArrowRight className="w-5 h-5" />
          </button>
          <div className="relative mt-6 flex items-center justify-center gap-6 text-sm text-white/60">
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4" /> Free to start</span>
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4" /> No credit card</span>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}

const SectionHeader = ({ overline, title, subtitle }) => (
  <div className="max-w-2xl">
    <span className="text-xs font-bold uppercase tracking-[0.2em] text-ho-primary">{overline}</span>
    <h2 className="font-display mt-3 text-3xl font-bold tracking-tight md:text-4xl">{title}</h2>
    {subtitle && <p className="mt-3 text-lg text-ho-muted">{subtitle}</p>}
  </div>
);

const BentoCard = ({ children, className = "", ...rest }) => (
  <div className={`rounded-3xl border border-black/5 bg-white p-6 ${className}`} {...rest}>{children}</div>
);

const MiniFeature = ({ icon: Icon, title, body }) => (
  <div className="flex h-full flex-col justify-between">
    <div className="grid place-items-center w-11 h-11 rounded-2xl bg-ho-primary/10">
      <Icon className="w-5 h-5 text-ho-primary" />
    </div>
    <div>
      <div className="font-display text-lg font-bold">{title}</div>
      <p className="mt-1 text-sm text-ho-muted">{body}</p>
    </div>
  </div>
);

const Stat = ({ value, label }) => (
  <div className="text-center">
    <div className="font-display text-2xl font-extrabold tracking-tight text-ho-ink">{value}</div>
    <div className="text-xs font-medium text-ho-muted">{label}</div>
  </div>
);

const Divider = () => <div className="hidden h-8 w-px bg-black/10 sm:block" />;
